import React, { useEffect, useState, useCallback } from 'react';
import { Globe, Link as LinkIcon, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

// Admin-only "Public Listing" controls slotted inside PropertiesPanel.
// Lets the CEO toggle is_public, edit slug + marketing copy, and preview/copy the public URL.
export default function PublicListingTab({ api, property, onSaved }) {
  const [draft, setDraft] = useState({
    is_public: false, slug: '', tagline: '', headline: '', location: '',
    contact_email: '', contact_phone: '', listing_blurb: '',
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!property) return;
    setDraft({
      is_public: !!property.is_public,
      slug: property.slug || slugify(property.name || ''),
      tagline: property.tagline || '',
      headline: property.headline || '',
      location: property.location || '',
      contact_email: property.contact_email || '',
      contact_phone: property.contact_phone || '',
      listing_blurb: property.listing_blurb || '',
    });
  }, [property]);

  const save = useCallback(async () => {
    if (!property) return;
    if (draft.is_public && !draft.slug) {
      toast.error('A URL slug is required to publish');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/properties/${property.id}`, {
        is_public: draft.is_public,
        slug: slugify(draft.slug),
        tagline: draft.tagline,
        headline: draft.headline,
        location: draft.location,
        contact_email: draft.contact_email,
        contact_phone: draft.contact_phone,
        listing_blurb: draft.listing_blurb,
      });
      toast.success(draft.is_public ? 'Listing is live' : 'Saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  }, [api, property, draft, onSaved]);

  if (!property) return null;

  const publicUrl = draft.slug ? `${window.location.origin}/property/${slugify(draft.slug)}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="ceo-card-elevated p-5 space-y-4" data-testid="public-listing-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5" style={{ color: 'var(--accent)' }} />
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>Public Listing</h3>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest"
            style={{
              background: draft.is_public ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255, 255, 255, 0.06)',
              color: draft.is_public ? '#22c55e' : 'var(--text-dim)',
              border: `1px solid ${draft.is_public ? 'rgba(34, 197, 94, 0.3)' : 'var(--line-2)'}`,
            }}
            data-testid="public-status-badge">
            {draft.is_public ? 'Live' : 'Hidden'}
          </span>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="public-toggle">
          <input
            type="checkbox"
            checked={draft.is_public}
            onChange={e => setDraft({ ...draft, is_public: e.target.checked })}
            className="ceo-checkbox"
          />
          <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Publish listing</span>
        </label>
      </div>

      <p className="text-xs italic" style={{ color: 'var(--muted)' }}>
        When live, anyone with the URL can view available units and submit an inquiry — no login required. Inquiries land in the Leads pipeline with source <code>public_listing</code>.
      </p>

      {/* URL preview */}
      {draft.slug && (
        <div className="rounded-md p-3 flex items-center gap-2 flex-wrap" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <LinkIcon className="h-4 w-4" style={{ color: 'var(--accent)' }} />
          <code className="text-xs flex-1 min-w-0 truncate" style={{ color: 'var(--text)' }} data-testid="public-url">{publicUrl}</code>
          <button onClick={copyLink} className="ceo-btn-secondary text-xs" data-testid="copy-public-url">
            {copied ? <Check className="h-3 w-3 inline mr-1 text-[#22c55e]" /> : <Copy className="h-3 w-3 inline mr-1" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          {draft.is_public && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="ceo-btn-primary text-xs"
              data-testid="open-public-url">
              <ExternalLink className="h-3 w-3 inline mr-1" /> Preview
            </a>
          )}
        </div>
      )}

      {/* Marketing copy editor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="URL slug" hint="lowercase, no spaces — used in the URL">
          <input
            className="ceo-input"
            value={draft.slug}
            onChange={e => setDraft({ ...draft, slug: e.target.value.toLowerCase() })}
            placeholder="adams-way"
            data-testid="slug-input"
          />
        </Field>
        <Field label="Location" hint="city, state · shown above the title">
          <input
            className="ceo-input"
            value={draft.location}
            onChange={e => setDraft({ ...draft, location: e.target.value })}
            placeholder="Northern Virginia"
            data-testid="location-input"
          />
        </Field>
        <Field label="Tagline" hint="elegant one-liner under the title">
          <input
            className="ceo-input"
            value={draft.tagline}
            onChange={e => setDraft({ ...draft, tagline: e.target.value })}
            placeholder="Modern living. Built for the next generation."
            data-testid="tagline-input"
          />
        </Field>
        <Field label="Headline" hint="short sales hook">
          <input
            className="ceo-input"
            value={draft.headline}
            onChange={e => setDraft({ ...draft, headline: e.target.value })}
            placeholder="Now leasing & selling."
            data-testid="headline-input"
          />
        </Field>
        <Field label="Contact email">
          <input
            type="email"
            className="ceo-input"
            value={draft.contact_email}
            onChange={e => setDraft({ ...draft, contact_email: e.target.value })}
            placeholder="info@tnsprograms.org"
            data-testid="contact-email-input"
          />
        </Field>
        <Field label="Contact phone">
          <input
            className="ceo-input"
            value={draft.contact_phone}
            onChange={e => setDraft({ ...draft, contact_phone: e.target.value })}
            placeholder="571-555-0100"
            data-testid="contact-phone-input"
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="About / blurb" hint="long-form paragraph shown to visitors">
            <textarea
              rows={4}
              className="ceo-input"
              value={draft.listing_blurb}
              onChange={e => setDraft({ ...draft, listing_blurb: e.target.value })}
              placeholder="A four-story mixed-use community with residential and ground-floor commercial spaces…"
              data-testid="blurb-input"
            />
          </Field>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs italic" style={{ color: 'var(--muted)' }}>
          Tip: only units with status <strong>For Sale</strong>, <strong>Rental</strong>, <strong>Subsidized</strong>, or <strong>Rental or Sale</strong> appear publicly. Sold / Reserved / Do Not Sell units stay hidden.
        </p>
        <button onClick={save} disabled={saving} className="ceo-btn-primary disabled:opacity-50" data-testid="save-public-listing">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'var(--muted)' }}>{label}</span>
        {hint && <span className="text-[10px] italic opacity-60" style={{ color: 'var(--text-dim)' }}>{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function slugify(text) {
  return (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
